/**
 * i18n — the portal's language layer. Dependency-free on purpose.
 *
 *   import { t, useT } from '../i18n';
 *   const t = useT();                       // re-renders on language change
 *   <span>{t('nav.today', 'Today')}</span>  // Hindi if active, else the fallback
 *
 * `t(key, fallback)` returns the active language's string for `key`, or the
 * English fallback when the language is English or the key is untranslated —
 * so a page that has not been translated keeps reading exactly as it does
 * today, and a missing key is never a raw `nav.today` on screen.
 *
 * The active language is a MODULE singleton, not React state, for the same
 * reason `utils/businessConfig` is: the sidebar, the top bar and the page all
 * read it, and every one of them remounts on every navigation. A version
 * counter + subscribers (mirroring `configVersion` / `subscribeBusinessConfig`)
 * is what lets `useT()` re-render a mounted component the moment the toggle
 * is pressed, and what closes the race where the language is set before a
 * component has subscribed.
 *
 * Persisted in localStorage under `inikru_lang`; `<html lang>` follows it so
 * screen readers and the browser's font fallback pick the right script.
 */
import { useEffect, useState } from 'react';
import hi from './hi';

const STORAGE_KEY = 'inikru_lang';

export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'hi', label: 'हिन्दी', short: 'हि' },
];

// `en` is a passthrough — there is no English dictionary, the fallbacks ARE
// the English. Adding a language is one file here plus one row above.
const DICTS = { hi };
const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

const read = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED.has(v)) return v;
  } catch { /* private mode */ }
  return 'en';
};

const applyHtmlLang = (code) => {
  try { document.documentElement.lang = code; } catch { /* SSR / tests */ }
};

let _lang = read();
let _version = 0;
const _listeners = new Set();
applyHtmlLang(_lang);

export const getLang = () => _lang;
export const langVersion = () => _version;

export const subscribeLang = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

/** Switch language app-wide. Persists, updates <html lang>, notifies. */
export const setLang = (code) => {
  const next = SUPPORTED.has(code) ? code : 'en';
  if (next === _lang) return;
  _lang = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* quota */ }
  applyHtmlLang(next);
  _version += 1;
  _listeners.forEach((fn) => { try { fn(_version); } catch { /* a bad listener must not break the toggle */ } });
};

/**
 * THE call. `fallback` is the English string as written in the component;
 * `vars` fills `{name}` tokens in whichever string wins, so a translation can
 * reorder the sentence around the value.
 */
export const t = (key, fallback, vars) => {
  let out;
  if (_lang !== 'en') {
    const dict = DICTS[_lang];
    const s = dict && dict[key];
    if (typeof s === 'string' && s) out = s;
  }
  if (out === undefined) out = fallback !== undefined ? fallback : key;
  if (vars && typeof out === 'string') {
    out = out.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  }
  return out;
};

/** Hook form of `t` — subscribes the component to language changes. */
export const useT = () => {
  const [v, setV] = useState(langVersion);
  useEffect(() => {
    // Compare on mount as well as subscribing: the language can change
    // between render and effect, and a missed bump leaves the wrong words up.
    if (langVersion() !== v) setV(langVersion());
    return subscribeLang(setV);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return t;
};

/** `[lang, setLang]` for a switch control; re-renders on change. */
export const useLang = () => {
  const [v, setV] = useState(langVersion);
  useEffect(() => {
    if (langVersion() !== v) setV(langVersion());
    return subscribeLang(setV);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [_lang, setLang];
};

export default t;
